import React, { type SyntheticEvent } from "react";
import { shallowEqual } from "react-redux";
import { FINANCE_CALCULATOR } from "../../../../../../../../common-deprecated/constants";
import type { FinanceOptionType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import { NavStep, navStepsOrder } from "../../../../../../shared-logic/utils/Navigation";
import FinanceInsuranceCalculatorLink from "../../../../../utils/FinanceInsuranceCalculatorLink";
import * as Styles from "../styles/Basket";
import useStepUrl from "../../../../../hooks/shared/actions/useStepUrl";
import { BnbLabel, nextLabels, useBnbOptionalLabel } from "../../../../../utils/labels";
import Preloader, * as PreloaderStyles from "../../../../../../../../common-deprecated/styles/v2/lexus/custom/Preloader";
import useCtaList from "../../../../../hooks/shared/actions/useCtaList";
import CtaButton from "../../cta/CtaButton";
import ButtonPrimary from "../../../../../../../../common-deprecated/components/aem/lexus/button/ButtonPrimary";
import { showServicesStep } from "../../../../../utils/settings/bnbSettingUtils";
import { enabledServicesSelector } from "../../../../../utils/selectorUtils";
import { useBnbSelector } from "../../../../../redux/store";
import { trackNavGoToStep } from "../../../../../utils/tracking";
import { ButtonOutlineLight } from "../../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import IconWrapper from "../../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import Icon from "../../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { Ellipsis } from "../../../../../../../../common-deprecated/styles/v2/lexus/utilities/Ellipsis";
import IconFinance from "../../../../../../../../common-deprecated/svg/lexus/aem/union.svg";
import { useFinanceInsuranceNavigation } from "../../../../../hooks/shared/useFinanceInsurance";
import ButtonCta, { CtaIcon } from "../../../../../../../../common-deprecated/components/aem/lexus/button/ButtonCta";
import { canNavigateToBuyStep } from "../../../../../../../shared-logic/utils/checkoutUtils";
import type { FinanceRateStateType } from "../../../../../redux/reducers/FinanceRateReducer";

type PropsType = {
    step: NavStep;
    financeOption: FinanceOptionType;
};

const BasketCtas = ({ step, financeOption }: PropsType): JSX.Element => {
    const ctas = useCtaList("summaryBasketCta");

    const finance = useBnbSelector((state) => state.finance);
    const config = useBnbSelector((state) => state.save);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const basket = useBnbSelector((state) => state.basket);
    const commonSettings = useBnbSelector((state) => state.commonSettings);

    const { showFinance } = useBnbSelector(enabledServicesSelector, shallowEqual);
    const { saving: tyCodeSaving, error: tyCodeSavingFailed } = config;

    const showServices = showServicesStep(bnbSettings, commonSettings);
    const showCheckoutDisclaimer = useBnbOptionalLabel("checkoutDisclaimer");

    // labels and target for required next button cta (similar to the one in BottomNavigation)
    let nextTarget = navStepsOrder[navStepsOrder.indexOf(step) + 1];
    if (!showServices && nextTarget === NavStep.Services) {
        // Skip services on Build V1 or when services step is disabled
        nextTarget = NavStep.Summary;
    }
    const { href: nextUrl, onClick } = useStepUrl(nextTarget);
    const nextLabel = nextLabels[nextTarget]!;
    const showNext =
        nextTarget !== NavStep.Buy ||
        canNavigateToBuyStep<FinanceRateStateType>(
            bnbSettings.enableBuy,
            bnbSettings.allowedCheckoutFinanceProduct,
            finance,
            financeOption,
            basket,
        );
    const onNextClick = (event: SyntheticEvent): void => {
        trackNavGoToStep(step, nextTarget);
        onClick(event);
    };

    const { onClick: onFinanceClick, financeLabelKey } = useFinanceInsuranceNavigation(step);

    return (
        <>
            {showFinance &&
                financeOption === "monthly" &&
                (!bnbSettings?.showFinanceInsuranceInPage && !bnbSettings?.enableFinanceCompare ? (
                    <FinanceInsuranceCalculatorLink
                        type={FINANCE_CALCULATOR}
                        customButton={(props) => (
                            <ButtonCta onClick={() => props.onClick()} iconVariant={CtaIcon.Finance}>
                                <BnbLabel label={props.label} />
                            </ButtonCta>
                        )}
                    />
                ) : (
                    <ButtonOutlineLight fullWidth onClick={($event) => onFinanceClick($event, "cc-panel")}>
                        <IconWrapper>
                            <IconFinance />
                        </IconWrapper>
                        <Ellipsis maxWidth="100%">
                            <BnbLabel label={financeLabelKey} />
                        </Ellipsis>
                        <IconWrapper>
                            <Icon variant="arrowRight" />
                        </IconWrapper>
                    </ButtonOutlineLight>
                ))}

            {/* render loader when tyCode is saving */}
            {tyCodeSaving && (
                <PreloaderStyles.PreLoaderWrapper hasBorder hasButtonHeight>
                    <Preloader size={30} />
                </PreloaderStyles.PreLoaderWrapper>
            )}

            {/* render error when saving tyCode failed */}
            {tyCodeSavingFailed && <BnbLabel label="errorSavingConfig" />}

            {/* Do not render cta when buy step is disabled */}
            {step !== NavStep.Summary && showNext && !tyCodeSaving && !tyCodeSavingFailed && (
                <ButtonPrimary href={nextUrl} onClick={onNextClick} fullWidth>
                    <Ellipsis maxWidth="100%">
                        <BnbLabel
                            label={financeOption === "monthly" ? (`${nextLabel}Monthly` as any) : nextLabel}
                            fallbackLabel={nextLabel}
                        />
                    </Ellipsis>
                </ButtonPrimary>
            )}

            {step === NavStep.Summary && (
                <>
                    {!tyCodeSaving && !tyCodeSavingFailed && ctas[0] && (
                        <CtaButton width={1} linkType="primary" config={ctas[0]}>
                            {ctas[0].title}
                        </CtaButton>
                    )}
                    {showCheckoutDisclaimer && (
                        <>
                            <Styles.DisclaimerWrapper>
                                <BnbLabel
                                    label={
                                        financeOption === "cash" ? "checkoutDisclaimer" : "checkoutDisclaimerMonthly"
                                    }
                                    fallbackLabel="checkoutDisclaimer"
                                />
                            </Styles.DisclaimerWrapper>
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default BasketCtas;
